/**
 * SenderProfiles — review and edit how each SENDER's files are read on import.
 *
 * A profile is per (sender, file type): the document kind their files are
 * (digital, scanned, handwritten…), the identifiers that recognise them, and
 * the instructions the reading follows ("quantities are in BOX"). The AI
 * writes a DRAFT after a sender's first saved import; a person reviews and
 * edits it here, and from then on the text is theirs — a later AI analysis is
 * only offered (`suggested`), never written over it.
 *
 * A prompt can be for ONE document type of theirs (their orders and their
 * return claims read differently) or for any type (`*`). The KEYWORDS tab
 * holds what recognises a document's sender and type before it is read
 * (`KeywordRegistry`).
 *
 * Opened from the import window by people holding the `import_profiles`
 * right (admin / func-admin always). Every host supplies the transport.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, ArrowLeft, Loader2, Plus, Sparkles, Trash2, UserRound } from 'lucide-react'
import { KeywordRegistry } from './KeywordRegistry'
import {
  importErrorInfo, type ImportSenderProfile, type RecordImportTarget, type RecordImportTransport,
} from './types'

const FILE_KINDS: Array<{ value: string; label: string }> = [
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Picture / scan' },
  { value: 'docx', label: 'Word' },
  { value: 'spreadsheet', label: 'Spreadsheet' },
]
const DOC_KINDS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Detect each time' },
  { value: 'digital', label: 'Digital (computer-generated)' },
  { value: 'scanned_print', label: 'Scanned printout' },
  { value: 'handwritten', label: 'Handwritten' },
  { value: 'mixed', label: 'Printed form filled in by hand' },
  { value: 'photo', label: 'Phone photo' },
  { value: 'printout', label: 'Photographed printout' },
  { value: 'spreadsheet', label: 'Spreadsheet' },
]
const kindLabel = (v?: string) => DOC_KINDS.find((k) => k.value === v)?.label || v || '—'
const fileLabel = (v?: string) => FILE_KINDS.find((k) => k.value === v)?.label || v || '—'

interface Draft {
  sender: string
  file_kind: string
  /** `*` = any document type. */
  doc_type: string
  doc_kind: string
  identifiers: string
  instructions: string
  isNew: boolean
  suggested?: string
  hasPassword?: boolean
  /** A new PDF password to store ("" = leave as is). Write-only. */
  password: string
  removePassword?: boolean
}

export function SenderProfiles({ target, transport, senderLabel, onBack }: {
  target: RecordImportTarget
  transport: RecordImportTransport
  /** What the sender field is called (e.g. "Customer"). */
  senderLabel: string
  onBack: () => void
}) {
  const describe = transport.describeError ?? importErrorInfo
  const [profiles, setProfiles] = useState<ImportSenderProfile[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [filter, setFilter] = useState('')
  const [tab, setTab] = useState<'prompts' | 'keywords'>('prompts')
  // The document types the keyword registry knows — what a prompt can be for.
  const [docTypes, setDocTypes] = useState<Array<{ code: string; label: string }>>([])
  useEffect(() => {
    if (!transport.listRules) return
    let cancelled = false
    transport.listRules(target)
      .then((res) => { if (!cancelled) setDocTypes(res.doc_types || []) })
      .catch((e) => { console.info('[SenderProfiles] document types unavailable', e) })
    return () => { cancelled = true }
  }, [transport, target, tab])
  const typeLabel = (code?: string) => (!code || code === '*') ? 'Any'
    : docTypes.find((d) => d.code === code)?.label || code.replace(/_/g, ' ')

  const load = useCallback(async () => {
    if (!transport.listProfiles) return
    try {
      const res = await transport.listProfiles(target)
      setProfiles(res.profiles || [])
      setError(null)
    } catch (e) {
      console.warn('[SenderProfiles] could not load the profiles', e)
      setError(describe(e, 'The sender profiles could not be loaded.').message)
    }
  }, [transport, target, describe])

  useEffect(() => { void load() }, [load])

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return (profiles || []).filter((p) => !q
      || `${p.sender_code} ${p.sender_display || ''}`.toLowerCase().includes(q))
  }, [profiles, filter])

  const edit = (p: ImportSenderProfile) => setDraft({
    sender: p.sender_code, file_kind: p.file_kind, doc_type: p.doc_type || '*', doc_kind: p.doc_kind || '',
    identifiers: (p.identifiers || []).join('\n'), instructions: p.instructions || '',
    isNew: false, suggested: p.suggested && p.suggested !== p.instructions ? p.suggested : undefined,
    hasPassword: !!p.has_password, password: '',
  })

  const save = async () => {
    if (!draft || !transport.saveProfile) return
    if (!draft.sender.trim()) { setError(`Choose the ${senderLabel.toLowerCase()}.`); return }
    setBusy(true)
    try {
      await transport.saveProfile(target, {
        sender: draft.sender.trim(), file_kind: draft.file_kind, doc_kind: draft.doc_kind,
        doc_type: draft.doc_type || '*', instructions: draft.instructions,
        identifiers: draft.identifiers.split('\n').map((x) => x.trim()).filter(Boolean),
      })
      if (transport.setProfilePassword && draft.file_kind === 'pdf'
          && (draft.password || draft.removePassword)) {
        await transport.setProfilePassword(target, {
          sender: draft.sender.trim(), file_kind: 'pdf', password: draft.removePassword ? '' : draft.password,
        })
      }
      setDraft(null)
      await load()
    } catch (e) {
      console.warn('[SenderProfiles] could not save the profile', e)
      setError(describe(e, 'The profile could not be saved.').message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (p: ImportSenderProfile) => {
    if (!transport.deleteProfile) return
    setBusy(true)
    try {
      await transport.deleteProfile(target, p.sender_code, p.file_kind, p.doc_type || '*')
      setDraft(null)
      await load()
    } catch (e) {
      console.warn('[SenderProfiles] could not delete the profile', e)
      setError(describe(e, 'The profile could not be removed.').message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section style={card} aria-label="Sender profiles">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button type="button" onClick={draft ? () => setDraft(null) : onBack} style={btn}>
          <ArrowLeft size={14} /> {draft ? 'All profiles' : 'Back to the import'}
        </button>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          {draft ? (draft.isNew ? 'New sender profile'
            : `${draft.sender} · ${fileLabel(draft.file_kind)} · ${typeLabel(draft.doc_type)}`)
            : 'Sender profiles'}
        </h2>
        <div style={{ flex: 1 }} />
        {!draft && tab === 'prompts' && transport.saveProfile && (
          <button type="button" style={btn} onClick={() => setDraft({
            sender: '', file_kind: 'pdf', doc_type: '*', doc_kind: '', identifiers: '', instructions: '',
            isNew: true, password: '',
          })}><Plus size={14} /> Add a profile</button>
        )}
      </div>
      {!draft && transport.listRules && (
        <div role="tablist" style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
          {([['prompts', 'Prompts'], ['keywords', 'Keywords']] as const).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k}
              style={{ ...btn, ...(tab === k ? tabOn : {}) }} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
      )}
      {!draft && tab === 'keywords' && (
        <KeywordRegistry target={target} transport={transport} senderLabel={senderLabel} />
      )}
      {(draft || tab === 'prompts') && <>
      <p style={note}>
        How each {senderLabel.toLowerCase()}&apos;s files are read: what kind of document they send, the text
        that recognises them, and instructions such as &ldquo;quantities are in BOX&rdquo;. The AI drafts a
        profile after a sender&apos;s first saved import; once you edit it, your text is kept.
      </p>
      {error && <div role="alert" style={errorBox}><AlertCircle size={15} /> {error}</div>}

      {!draft && tab === 'prompts' && (
        <>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...input, marginBottom: 8 }}
            placeholder={`Find a ${senderLabel.toLowerCase()}…`} aria-label="Find a sender" />
          {profiles === null && !error && <div style={note}><Loader2 size={14} /> Loading…</div>}
          {profiles && shown.length === 0 && (
            <div style={note}>No profiles yet. They are drafted automatically after a sender&apos;s first import.</div>
          )}
          {shown.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  {[senderLabel, 'Document type', 'File type', 'Their documents', 'Instructions', 'Status', ''].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={`${p.sender_code}|${p.doc_type || '*'}|${p.file_kind}`} style={{ cursor: 'pointer' }} onClick={() => edit(p)}>
                    <td style={td}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <UserRound size={14} /> {p.sender_display || p.sender_code}
                      </span>
                      {p.sender_display && <div style={sub}>{p.sender_code}</div>}
                    </td>
                    <td style={td}>{typeLabel(p.doc_type)}</td>
                    <td style={td}>{fileLabel(p.file_kind)}</td>
                    <td style={td}>{kindLabel(p.doc_kind)}</td>
                    <td style={{ ...td, maxWidth: 320 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.instructions || (p.file_kind === 'spreadsheet' ? 'Remembers the column map' : '—')}
                      </div>
                    </td>
                    <td style={td}>
                      <span style={badge(p.status === 'draft')}>
                        {p.status === 'draft' ? 'AI draft — review' : p.source === 'user' ? 'Reviewed' : 'Active'}
                      </span>
                      <div style={sub}>{p.samples || 0} import{(p.samples || 0) === 1 ? '' : 's'}
                        {p.has_password ? ' · password stored' : ''}</div>
                    </td>
                    <td style={td} onClick={(e) => e.stopPropagation()}>
                      {transport.deleteProfile && (
                        <button type="button" style={iconBtn} disabled={busy} aria-label="Remove profile"
                          title="Remove this profile" onClick={() => void remove(p)}><Trash2 size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {draft && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={field}>{senderLabel}
              <input value={draft.sender} disabled={!draft.isNew} style={input}
                onChange={(e) => setDraft({ ...draft, sender: e.target.value })}
                placeholder="Code or name" />
            </label>
            <label style={field}>File type
              <select value={draft.file_kind} disabled={!draft.isNew} style={input}
                onChange={(e) => setDraft({ ...draft, file_kind: e.target.value })}>
                {FILE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
            <label style={field}>For document type
              <select value={draft.doc_type || '*'} disabled={!draft.isNew} style={input}
                onChange={(e) => setDraft({ ...draft, doc_type: e.target.value })}>
                <option value="*">Any document type</option>
                {docTypes.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
                {draft.doc_type && draft.doc_type !== '*' && !docTypes.some((d) => d.code === draft.doc_type) && (
                  <option value={draft.doc_type}>{typeLabel(draft.doc_type)}</option>
                )}
              </select>
            </label>
            <label style={field}>Their documents are
              <select value={draft.doc_kind} style={input}
                onChange={(e) => setDraft({ ...draft, doc_kind: e.target.value })}>
                {DOC_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
          </div>
          <label style={field}>Instructions for reading their files
            <textarea value={draft.instructions} rows={7} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }}
              onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
              placeholder={'- Quantities are always in BOX unless the line says otherwise.\n- The "Part No." column is their item code.'} />
          </label>
          {draft.suggested && (
            <div style={suggestBox}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                <Sparkles size={14} /> The AI suggests (from the latest import)
              </div>
              <pre style={{ margin: '6px 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{draft.suggested}</pre>
              <button type="button" style={btn}
                onClick={() => setDraft({ ...draft, instructions: draft.suggested || '', suggested: undefined })}>
                Use the suggestion
              </button>
            </div>
          )}
          {draft.file_kind === 'pdf' && transport.setProfilePassword && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={field}>PDF password {draft.hasPassword && !draft.removePassword ? '(one is stored)' : ''}
                <input type="password" autoComplete="new-password" value={draft.password} style={input}
                  disabled={!!draft.removePassword}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                  placeholder={draft.hasPassword ? 'Leave blank to keep the stored one' : 'Their PDFs open with…'} />
              </label>
              {draft.hasPassword && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 8 }}>
                  <input type="checkbox" checked={!!draft.removePassword}
                    onChange={(e) => setDraft({ ...draft, removePassword: e.target.checked, password: '' })} />
                  Remove the stored password
                </label>
              )}
            </div>
          )}
          <label style={field}>Text that recognises them (one per line — company name, tax ID, email domain)
            <textarea value={draft.identifiers} rows={3} style={{ ...input, fontFamily: 'inherit', resize: 'vertical' }}
              onChange={(e) => setDraft({ ...draft, identifiers: e.target.value })} />
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" style={btn} onClick={() => setDraft(null)} disabled={busy}>Cancel</button>
            <button type="button" style={primaryBtn} onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </div>
      )}
      </>}
    </section>
  )
}

const card: React.CSSProperties = {
  background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #e6e5e0)',
  borderRadius: 12, padding: 16,
}
const note: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary, #5e6472)', margin: '0 0 10px' }
const sub: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }
const th: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', fontSize: 13, fontWeight: 600,
  color: 'var(--color-text-secondary, #5e6472)', borderBottom: '1px solid var(--color-border, #e6e5e0)',
}
const td: React.CSSProperties = { padding: '8px', borderBottom: '1px solid var(--color-border, #f0efea)', verticalAlign: 'top' }
const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 14, borderRadius: 6,
  border: '1px solid var(--color-border-strong, var(--color-border, #d5d7dc))',
  background: 'var(--color-input-bg, var(--color-bg, #fff))', color: 'var(--color-text, #15171c)',
}
const field: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: '1 1 200px',
  color: 'var(--color-text-secondary, #5e6472)',
}
const btn: React.CSSProperties = {
  height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', fontSize: 14,
  borderRadius: 8, border: '1px solid var(--color-border, #e6e5e0)', background: 'var(--color-bg, #fff)',
  color: 'inherit', cursor: 'pointer',
}
const primaryBtn: React.CSSProperties = {
  ...btn, background: 'var(--color-accent, #1d4ed8)', borderColor: 'var(--color-accent, #1d4ed8)', color: '#fff',
}
const iconBtn: React.CSSProperties = { ...btn, width: 32, padding: 0, justifyContent: 'center' }
const tabOn: React.CSSProperties = {
  borderColor: 'var(--color-accent, #1d4ed8)', color: 'var(--color-accent, #1d4ed8)', fontWeight: 600,
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', marginBottom: 10, borderRadius: 8,
  background: 'rgba(217,45,32,0.08)', color: '#b42318', fontSize: 14,
}
const suggestBox: React.CSSProperties = {
  padding: 10, borderRadius: 8, fontSize: 13, background: 'rgba(47,95,227,0.06)',
  border: '1px solid rgba(47,95,227,0.25)', color: 'var(--color-text, #15171c)',
}
function badge(warn: boolean): React.CSSProperties {
  return {
    fontSize: 13, padding: '1px 8px', borderRadius: 999,
    background: warn ? 'rgba(217,138,18,0.14)' : 'rgba(22,101,52,0.12)',
    color: warn ? '#a15c07' : '#166534',
  }
}
